import { ref, computed } from 'vue';
import { useTheme } from './useTheme';
import type { ThemeName } from '../themes';

export interface TenantTheme {
  preset_theme_name: ThemeName | 'custom';
  css_overrides: Record<string, string> | null;
  terminology_preset: string;
  terminology_overrides: Record<string, string> | null;
  brand_name: string | null;
  logo_url: string | null;
}

const currentTenantTheme = ref<TenantTheme | null>(null);

/**
 * Applique les CSS var overrides du tenant sur :root.
 * Réversible : un appel avec null nettoie les overrides précédents.
 */
function applyCssOverrides(overrides: Record<string, string> | null) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  // 1. Nettoyer les overrides précédents (préfixés via data-attribute pour les retrouver)
  const prev = root.getAttribute('data-tenant-overrides');
  if (prev) {
    try {
      const prevKeys = JSON.parse(prev) as string[];
      for (const key of prevKeys) {
        root.style.removeProperty(key);
      }
    } catch {
      // ignore
    }
  }

  // 2. Appliquer les nouveaux
  if (overrides) {
    const keys = Object.keys(overrides);
    for (const key of keys) {
      if (key.startsWith('--ds-')) {
        root.style.setProperty(key, overrides[key]);
      }
    }
    root.setAttribute('data-tenant-overrides', JSON.stringify(keys));
  } else {
    root.removeAttribute('data-tenant-overrides');
  }
}

export function useTenantTheme() {
  const { setTheme } = useTheme();

  /**
   * Applique le theme tenant complet : preset DS + overrides CSS.
   * Appelé au login (depuis auth store) et au change manuel (depuis page admin).
   */
  function applyTenantTheme(theme: TenantTheme | null) {
    currentTenantTheme.value = theme;

    if (!theme) {
      applyCssOverrides(null);
      return;
    }

    // 1. Appliquer le preset DS (atelier / premium / chantier)
    //    Si 'custom', on garde le preset courant et on applique uniquement les overrides
    if (theme.preset_theme_name !== 'custom') {
      setTheme(theme.preset_theme_name as ThemeName);
    }

    // 2. Appliquer les overrides CSS vars (par-dessus le preset)
    applyCssOverrides(theme.css_overrides);
  }

  function clearTenantTheme() {
    applyTenantTheme(null);
  }

  return {
    currentTenantTheme: computed(() => currentTenantTheme.value),
    applyTenantTheme,
    clearTenantTheme,
  };
}
